import { DeleteOutlined, ClockCircleFilled } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Form,
  InputNumber,
  Select,
  Tag,
  Typography,
} from 'antd';
import cn from 'classnames';
import React from 'react';

import { servicesColumns } from '@app/components/RecordModal/components/RecordDetails/data/constants';
import { useServices } from '@app/components/RecordModal/components/RecordDetails/hooks';
import type { ServicesType } from '@app/components/RecordModal/components/RecordDetails/types';
import { AutoAdd } from '@app/components/RecordModal/components/RecordDetails/ui';
import { NumberInputWithMask } from '@app/components/RecordModal/components/RecordDetails/ui/index';
import { getEffectiveServiceDiscount } from '@app/components/RecordModal/components/RecordDetails/utils';
import {
  getSubscriptionQuantityHint,
  getSubscriptionQuantityLimitError,
} from '@app/components/RecordModal/components/RecordDetails/utils/subscriptionQuantityLimit';
import { WithTooltip } from '@app/components/WithTooltip';
import { withTestId, divideAmount } from '@app/utils';

import '@app/components/RecordModal/components/RecordDetails/styles/Services.desktop.scss';

const { Item, List } = Form;
const { Text } = Typography;
const PF = 'services';
const SUBSCRIPTION_PRICE_HINT = 'Услуга с абонемента: цена 0, скидки нет';
const SELECT_SERVICE_HINT = 'Сначала выберите услугу';

export const Services: ServicesType = ({
  totalDuration,
  serviceOptions,
  branchUserOptions,
  getSubscriptionQuantityLimit,
  disabled,
}) => {
  const {
    getServiceSelected,
    handleServiceChange,
    deleteService,
    totalAmount,
    totalQuantity,
    shouldUpdate,
    getFieldValue,
    handleTotalDuration,
    validateFields,
    getFilteredOptionsForField,
    allServicesSelected,
    handleDiscountCurrencyChange,
    handleDiscountPercentChange,
    handleQuantityChange,
    handlePriceChange,
  } = useServices({ serviceOptions, disabled });

  return (
    <div className={PF}>
      <List name={PF}>
        {(fields, { add, remove }) => (
          <>
            <AutoAdd add={add} fields={fields} />
            <div className={`${PF}__scroll-wrapper`}>
              <table className={`${PF}__table`}>
                <thead>
                  <tr>
                    {servicesColumns.map(({ id, title, className }) => (
                      <th key={id} className={`${PF}__th ${PF}__${className}`}>
                        {title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fields.map(({ key, ...restField }) => {
                    const { name } = restField;
                    const serviceSelected = getServiceSelected(name);
                    // Услуга с абонемента: цена 0, цену, скидку и саму услугу
                    // не меняем, количество — от 1 до «в записи + свободные»
                    // (us/11427, task/11536).
                    const isSubscriptionService = Boolean(
                      getFieldValue(['services', name, 'clientSubscriptionId'])
                    );
                    const subscriptionQuantityLimit = isSubscriptionService
                      ? getSubscriptionQuantityLimit(
                          getFieldValue(['services', name])
                        )
                      : null;
                    // Подсказка о пределе — только у открытого поля.
                    const quantityHint = disabled
                      ? null
                      : getSubscriptionQuantityHint(subscriptionQuantityLimit);
                    return (
                      <tr key={key} className={`${PF}__row`}>
                        <td className={`${PF}__cell`}>
                          <div className={`${PF}__cell-container`}>
                            <WithTooltip
                              show={true}
                              title={
                                isSubscriptionService
                                  ? 'Услуга с абонемента исключена из расчёта системы лояльности'
                                  : serviceSelected
                                  ? 'Исключить из расчёта системы лояльности'
                                  : 'Сначала выберите услугу'
                              }
                            >
                              <Item
                                label=""
                                className={`${PF}__checkbox`}
                                name={[name, 'isExcludedFromLoyaltyDiscount']}
                                valuePropName="checked"
                              >
                                <Checkbox
                                  disabled={
                                    disabled ||
                                    !serviceSelected ||
                                    isSubscriptionService
                                  }
                                  data-testid="loyalty-exclusion-checkbox"
                                />
                              </Item>
                            </WithTooltip>
                          </div>
                        </td>
                        <td className={`${PF}__cell`}>
                          <div className={`${PF}__cell-container`}>
                            <Item
                              {...restField}
                              name={[name, 'service']}
                              initialValue={null}
                            >
                              <Select
                                showSearch
                                optionFilterProp="name"
                                data-testid="service-select"
                                className={cn(`${PF}__service-select`, {
                                  [`${PF}__service-select--subscription`]:
                                    isSubscriptionService,
                                })}
                                disabled={disabled || isSubscriptionService}
                                placeholder="Выберите услугу"
                                options={getFilteredOptionsForField(name)}
                                onChange={(value) =>
                                  handleServiceChange(name, value)
                                }
                                labelRender={(label) => {
                                  const selectedOptionId = label.value;
                                  let optionLabel = '';
                                  serviceOptions.forEach(({ options }) => {
                                    options?.forEach((opt) => {
                                      if (selectedOptionId === opt?.value) {
                                        optionLabel =
                                          opt?.label?.toString() || '';
                                      }
                                    });
                                  });
                                  // Услуги нет у нового исполнителя: название
                                  // берём из строки, а не показываем пусто.
                                  return (
                                    <div>
                                      {optionLabel ||
                                        getFieldValue([
                                          'services',
                                          name,
                                          'name',
                                        ]) ||
                                        ''}
                                    </div>
                                  );
                                }}
                                popupRender={(menu) => (
                                  <div data-testid="service-dropdown">
                                    {React.cloneElement(menu, {
                                      options: getFilteredOptionsForField(
                                        name
                                      ).map((group) => ({
                                        ...group,
                                        options: group.options.filter(
                                          (opt) =>
                                            opt.value !==
                                            getFieldValue([
                                              'services',
                                              name,
                                              'service',
                                            ])
                                        ),
                                      })),
                                    })}
                                  </div>
                                )}
                              />
                            </Item>
                            {isSubscriptionService && (
                              <Tag
                                color="blue"
                                className={`${PF}__subscription-tag`}
                                data-testid="subscription-service-tag"
                              >
                                по абонементу
                              </Tag>
                            )}
                          </div>
                        </td>
                        <td className={`${PF}__cell`}>
                          <div className={`${PF}__cell-container`}>
                            <WithTooltip
                              show={disabled || !serviceSelected}
                              title="Сначала выберите услугу"
                            >
                              <Item
                                {...restField}
                                name={[name, 'upsaleEmployee']}
                                initialValue={null}
                                shouldUpdate={shouldUpdate}
                              >
                                <Select
                                  options={branchUserOptions}
                                  disabled={!serviceSelected}
                                  showSearch
                                  allowClear
                                  optionFilterProp="label"
                                  data-testid="upsell-select"
                                  popupRender={withTestId('upsell-dropdown')}
                                />
                              </Item>
                            </WithTooltip>
                          </div>
                        </td>
                        <td className={`${PF}__cell`}>
                          <div className={`${PF}__cell-container`}>
                            <WithTooltip
                              show={
                                disabled ||
                                !serviceSelected ||
                                quantityHint !== null
                              }
                              title={quantityHint ?? SELECT_SERVICE_HINT}
                            >
                              <Item
                                {...restField}
                                name={[name, 'quantity']}
                                initialValue={0}
                                shouldUpdate={shouldUpdate}
                                rules={[
                                  { type: 'number' },
                                  {
                                    validator: (_, value) => {
                                      if (+value > 99) {
                                        return Promise.reject(
                                          'Не может быть больше 99'
                                        );
                                      }

                                      if (
                                        subscriptionQuantityLimit &&
                                        +value > subscriptionQuantityLimit.max
                                      ) {
                                        return Promise.reject(
                                          getSubscriptionQuantityLimitError(
                                            subscriptionQuantityLimit
                                          )
                                        );
                                      }

                                      if (!serviceSelected) {
                                        return Promise.resolve();
                                      }

                                      if (+value >= 1) {
                                        return Promise.resolve();
                                      }

                                      return Promise.reject(
                                        'Не может быть меньше 1'
                                      );
                                    },
                                  },
                                ]}
                              >
                                {/* Без max: InputNumber при вводе больше max не
                                    вызывает onChange и молча ставит максимум,
                                    а предел показывает ошибка валидатора. */}
                                <InputNumber
                                  min={0}
                                  step={1}
                                  maxLength={20}
                                  disabled={disabled || !serviceSelected}
                                  data-testid="quantity-input"
                                  onChange={(value) => {
                                    handleTotalDuration();
                                    validateFields([
                                      ['services', name, 'quantity'],
                                    ]);
                                    validateFields([
                                      ['services', name, 'discountCurrency'],
                                    ]);
                                    handleQuantityChange(value || 0, name);
                                  }}
                                />
                              </Item>
                            </WithTooltip>
                          </div>
                        </td>
                        <td className={`${PF}__cell`}>
                          <div className={`${PF}__cell-container`}>
                            <WithTooltip
                              show={
                                disabled ||
                                !serviceSelected ||
                                isSubscriptionService
                              }
                              title={
                                isSubscriptionService
                                  ? SUBSCRIPTION_PRICE_HINT
                                  : SELECT_SERVICE_HINT
                              }
                            >
                              <Item
                                {...restField}
                                name={[name, 'price']}
                                initialValue={0}
                                shouldUpdate={shouldUpdate}
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      if (!serviceSelected) {
                                        return Promise.resolve();
                                      }
                                      if (+value >= 0) {
                                        return Promise.resolve();
                                      }
                                      return Promise.reject(
                                        'Не может быть меньше 0'
                                      );
                                    },
                                  },
                                ]}
                              >
                                <NumberInputWithMask
                                  disabled={
                                    disabled ||
                                    !serviceSelected ||
                                    isSubscriptionService
                                  }
                                  dataTestId="price-service-input"
                                  onChange={(value) => {
                                    handlePriceChange(value, name);
                                    validateFields([
                                      ['services', name, 'discountCurrency'],
                                    ]);
                                  }}
                                />
                              </Item>
                            </WithTooltip>
                          </div>
                        </td>
                        <td className={`${PF}__cell`}>
                          <div
                            className={`${PF}__cell-container discount-field`}
                          >
                            {disabled ? (
                              <Item shouldUpdate={shouldUpdate}>
                                {() => {
                                  const row =
                                    getFieldValue('services')?.[name] || {};

                                  return (
                                    <Text data-testid="discount-service-total">
                                      {divideAmount(
                                        getEffectiveServiceDiscount(row, true)
                                      )}
                                    </Text>
                                  );
                                }}
                              </Item>
                            ) : (
                              <>
                                <WithTooltip
                                  show={
                                    disabled ||
                                    !serviceSelected ||
                                    isSubscriptionService
                                  }
                                  title={
                                    isSubscriptionService
                                      ? SUBSCRIPTION_PRICE_HINT
                                      : SELECT_SERVICE_HINT
                                  }
                                >
                                  <Item
                                    {...restField}
                                    name={[name, 'discountCurrency']}
                                    initialValue={0}
                                    rules={[
                                      { type: 'number' },
                                      {
                                        validator: (_, value) => {
                                          if (!serviceSelected) {
                                            return Promise.resolve();
                                          }

                                          if (value < 0) {
                                            return Promise.reject(
                                              'Не может быть меньше 0'
                                            );
                                          }
                                          const price =
                                            getFieldValue([
                                              'services',
                                              name,
                                              'price',
                                            ]) || 0;

                                          const quantity =
                                            getFieldValue([
                                              'services',
                                              name,
                                              'quantity',
                                            ]) || 0;
                                          if (value > price * quantity) {
                                            return Promise.reject(
                                              'Не может быть больше цены'
                                            );
                                          }

                                          return Promise.resolve();
                                        },
                                      },
                                    ]}
                                    shouldUpdate={shouldUpdate}
                                  >
                                    <NumberInputWithMask
                                      disabled={
                                        disabled ||
                                        !serviceSelected ||
                                        isSubscriptionService
                                      }
                                      dataTestId="discount-service-price"
                                      onChange={(value) =>
                                        handleDiscountCurrencyChange(
                                          value,
                                          name
                                        )
                                      }
                                    />
                                  </Item>
                                </WithTooltip>
                                <WithTooltip
                                  show={
                                    disabled ||
                                    !serviceSelected ||
                                    isSubscriptionService
                                  }
                                  title={
                                    isSubscriptionService
                                      ? SUBSCRIPTION_PRICE_HINT
                                      : SELECT_SERVICE_HINT
                                  }
                                >
                                  <Item
                                    {...restField}
                                    name={[name, 'discountPercent']}
                                    initialValue={0}
                                    rules={[
                                      { type: 'number' },
                                      {
                                        validator: (_, value) => {
                                          if (!serviceSelected) {
                                            return Promise.resolve();
                                          }

                                          if (value < 0) {
                                            return Promise.reject(
                                              'Не может быть меньше 0'
                                            );
                                          }

                                          if (value > 100) {
                                            return Promise.reject(
                                              'Не может быть больше 100%'
                                            );
                                          }

                                          return Promise.resolve();
                                        },
                                      },
                                    ]}
                                    shouldUpdate={shouldUpdate}
                                  >
                                    <NumberInputWithMask
                                      disabled={
                                        disabled ||
                                        !serviceSelected ||
                                        isSubscriptionService
                                      }
                                      dataTestId="discount-percentage"
                                      suffix="%"
                                      onChange={(value) =>
                                        handleDiscountPercentChange(value, name)
                                      }
                                    />
                                  </Item>
                                </WithTooltip>
                              </>
                            )}
                          </div>
                        </td>
                        <td className={`${PF}__cell`}>
                          <Item shouldUpdate={shouldUpdate}>
                            {() => {
                              const row =
                                getFieldValue('services')?.[name] || {};
                              const quantity =
                                row.quantity >= 0 ? row.quantity : 0;
                              const price = row.price >= 0 ? row.price : 0;
                              const effectiveDiscount =
                                getEffectiveServiceDiscount(row, disabled);
                              const discount =
                                effectiveDiscount >= 0 ? effectiveDiscount : 0;
                              const total = +(
                                quantity * price -
                                discount
                              ).toFixed(2);
                              const formattedTotal =
                                total >= 0 ? divideAmount(total) : 0;
                              return (
                                <div className={`${PF}__total-cell`}>
                                  <Text data-testid="to-pay-service">
                                    {formattedTotal}
                                  </Text>
                                </div>
                              );
                            }}
                          </Item>
                        </td>
                        <td className={`${PF}__cell`}>
                          <Item>
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() => deleteService(name, remove)}
                              disabled={disabled || !serviceSelected}
                            />
                          </Item>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className={`${PF}__footer`}>
                    <td className={`${PF}__footer-cell`} />
                    <td
                      className={`${PF}__footer-cell ${PF}__footer-cell-name`}
                    >
                      <div className={`${PF}__footer-summary`}>
                        <div className={`${PF}__footer-summary-total-duration`}>
                          <div
                            className={`${PF}__footer-summary-total-duration-title`}
                            data-testid="total-text"
                          >
                            Итого:
                          </div>
                          <div
                            className={`${PF}__footer-summary-total-duration-value`}
                            data-testid="total-time"
                          >
                            <ClockCircleFilled />
                            {totalDuration.hours} ч {totalDuration.minutes} мин
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`${PF}__footer-cell`} />
                    <td
                      className={`${PF}__footer-cell ${PF}__footer-cell-quantity`}
                    >
                      <span data-testid="total-quantity">
                        {divideAmount(totalQuantity)} шт
                      </span>
                    </td>
                    <td className={`${PF}__footer-cell`} />
                    <td className={`${PF}__footer-cell`} />
                    <td
                      className={`${PF}__footer-cell ${PF}__footer-cell-total`}
                      data-testid="total-to-pay"
                    >
                      <span>{divideAmount(totalAmount)}</span>
                    </td>
                    <td className={`${PF}__footer-cell`} />
                  </tr>
                  <tr className={`${PF}__footer`}>
                    <td className={`${PF}__footer-cell`} />
                    <td
                      className={`${PF}__footer-cell ${PF}__footer-cell-name`}
                    >
                      {(fields.length === 0 ||
                        getServiceSelected(fields[fields.length - 1].name)) &&
                        !allServicesSelected && (
                          <Button
                            type="primary"
                            onClick={() => add()}
                            data-testid="add-service"
                            className={`${PF}__footer-summary-add`}
                            disabled={disabled}
                          >
                            Добавить услугу
                          </Button>
                        )}
                    </td>
                    <td className={`${PF}__footer-cell`} />
                    <td className={`${PF}__footer-cell`} />
                    <td className={`${PF}__footer-cell`} />
                    <td className={`${PF}__footer-cell`} />
                    <td className={`${PF}__footer-cell`} />
                    <td className={`${PF}__footer-cell`} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </List>
    </div>
  );
};
